// CustomerDetailTabs.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  IconButton,
  Tabs,
  Tab,
  Grid,
  TextField,
  MenuItem,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Skeleton,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MachinesTab from "./Tabs/MachinesTab";
import { ReactComponent as ChatSvg } from "../../assets/Chat.svg";
import UsersTab from "./Tabs/UsersTabs";
import LocationsTab from "./Tabs/LocationsTab";
import { useLocation } from "react-router-dom";
import { fetchCustomers } from "../KundenDashboard/api";
import { useResolvedImageSrc } from "../../utils/imageFallback";
import customerLogoFallback from "../../assets/DeltaTech.svg";

const getCustomerImageUrl = (customer) => {
  if (customer?.image?.url) {
    return customer.image.url;
  }

  if (customer?.logo?.url) {
    return customer.logo.url;
  }

  if (customer?.legalentitylogo?.url) {
    return customer.legalentitylogo.url;
  }

  if (customer?.customerportal?.logo?.url) {
    return customer.customerportal.logo.url;
  }

  return "";
};

/* ========================================
   Parent: Header + Tabs
======================================== */
export default function CustomerDetailTabs() {
  const [tab, setTab] = React.useState(0);
  const location = useLocation();
  const [customer, setCustomer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const customerNodeId = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("nodeid") ? decodeURIComponent(searchParams.get("nodeid")) : "";
  }, [location.search]);

  useEffect(() => {
    let active = true;

    setIsLoading(true);

    fetchCustomers()
      .then((customers) => {
        if (!active) {
          return;
        }

        const matchedCustomer = customerNodeId
          ? customers.find((entry) => entry.nodeid === customerNodeId)
          : customers[0] ?? null;

        setCustomer(matchedCustomer ?? null);
      })
      .catch(() => {
        if (active) {
          setCustomer(null);
        }
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [customerNodeId]);

  const resolvedCustomerImage = useResolvedImageSrc(
    getCustomerImageUrl(customer),
    customerLogoFallback
  );


  return (
    <Paper
      elevation={1}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: `1px solid #E6E8EB`,
        bgcolor: "#fff",
        mb:4
      }}
    >
      {/* Header */}
      <Box sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            {isLoading ? (
              <Skeleton variant="rounded" width={56} height={56} />
            ) : (
              <Box
                component="img"
                src={resolvedCustomerImage}
                alt={customer?.name ?? "Customer"}
                sx={{
                  width: 56,
                  height: 56,
                  objectFit: "contain",
                  borderRadius: 1,
                  flexShrink: 0,
                }}
              />
            )}

            {isLoading ? (
              <Skeleton variant="text" width={260} height={52} />
            ) : (
              <Typography variant="h4" sx={{
                color: "var(--color-primary)",
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontFamily: "Rubik",
                fontSize: "32px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "140%",
                letterSpacing: "0.15px",
              }}>
                {customer?.name ?? "—"}
              </Typography>
            )}
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<ChatSvg />}
              sx={{
                color: "var(--On-Primary-High-Emphasis, #FFF)",
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontFamily: "Roboto",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "120%",
                letterSpacing: "1.25px",
                textTransform: "uppercase",
              }}
            >
              NEUE NACHRICHT
            </Button>
            <IconButton>
              <MoreVertIcon />
            </IconButton>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={2} sx={{ mt: 1, color: "rgba(var(--color-primary-rgb), 0.55)", alignItems: "center" }}>
          <Typography sx={{
            color: "var(--On-Surface-Medium-Emphasis, rgba(0, 0, 0, 0.50))",
            fontFeatureSettings: "'liga' off, 'clig' off",
            fontFamily: "Roboto",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: "160%",
            letterSpacing: "0.25px",
          }}>
            <div style={{ display: "flex",alignItems:"center" }} >    <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: "#1976d2",
                marginRight:"3px"
              }}
            />
              &nbsp;Status: {customer?.active === false ? "Inaktiv" : "Aktiv"}
            </div>
          </Typography>
          <Typography sx={{
            color: "var(--On-Surface-Medium-Emphasis, rgba(0, 0, 0, 0.50))",
            fontFeatureSettings: "'liga' off, 'clig' off",
            fontFamily: "Roboto",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: "160%",
            letterSpacing: "0.25px",
          }}>
            |&nbsp;&nbsp;  Kundenportal:{" "}
            <span style={{ textDecoration: "underline", textDecorationStyle: "dotted" }}>
              {customer?.portal || "—"}
            </span>
          </Typography>
        </Stack>
      </Box>

      {/* Tabs */}
      <Box sx={{ bgcolor: "var(--color-primary)" }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          textColor="inherit"
          TabIndicatorProps={{ style: { backgroundColor: "#fff" } }}
          sx={{
            "& .MuiTab-root": { color: "#CFE1E8", textTransform: "none", fontWeight: 500, minHeight: 48 },
            "& .Mui-selected": { color: "#fff" },
          }}
        >
          <Tab label="MASCHINEN" sx={{ px: 2.8 }} />
          <Tab label="USER" disabled />
          <Tab label="STANDORTE" disabled/>
          <Tab label="RECHNUNGSADRESSEN" disabled />
        </Tabs>
      </Box>
      <Box
        sx={{
          width: "100%",
          height: "1px",
          position: "relative",
          backgroundColor: "rgba(0, 0, 0, 0.12)",
        }}
      />

      {/* Tab panels */}
      {tab === 0 && <MachinesTab customerNodeId={customerNodeId}/>}
       {tab === 1 && <UsersTab />}
      {tab === 2 && <LocationsTab />}
      {/* {tab === 3 && <RechnungsadressenTab />}  */}
    </Paper>
  );
}
